import cx from 'classnames';
import type { FieldProps } from 'formik';
import { Field, Formik } from 'formik';
import React, { useContext, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import * as Yup from 'yup';

import { createUserTrace } from '../../api/Optimise';
import { OnboardingContext } from '../../context/OnboardingContext';
import LoopSpinner from '../../ui/LoopSpinner';
import { RadioWrapper } from '../../ui/Styled';
import yesNoToBool from '../../utils/yesNoToBool';

const solarDiverterFormOptions = ['yes', 'no'] as const;
type SolarDiverterOption = (typeof solarDiverterFormOptions)[number];

interface FormValues {
  hasSolarDiverter: SolarDiverterOption;
}

const SolarDiverterContainer: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setProgressPercentage, hasSolarDiverter, setHasSolarDiverter } = useContext(OnboardingContext);

  const validationSchema = Yup.object({
    hasSolarDiverter: Yup.mixed<SolarDiverterOption>()
      .oneOf([...solarDiverterFormOptions])
      .required(),
  });

  const onSubmit = ({ hasSolarDiverter }: FormValues): void => {
    void createUserTrace('Solar Diverter', {
      additional_information: {
        has_solar_diverter: yesNoToBool(hasSolarDiverter),
      },
    });
    if (yesNoToBool(hasSolarDiverter)) {
      void navigate('/onboarding/solar-diverter-warning');
    } else {
      void navigate('/onboarding/electric-vehicle');
    }
  };

  useEffect(() => {
    setProgressPercentage(50);
  }, [setProgressPercentage]);

  return (
    <>
      <h1 className="mb-3">Do you have a solar diverter? </h1>
      <div>
        <p>A solar diverter uses excess solar to either heat hot water or charge an electric vehicle.</p>
        <p>
          <a href={theme.links.whyCantUseSolarDiverter}>More info about solar diverters</a>
        </p>
        <div className="text-left mt-4">
          <Formik<FormValues>
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            initialValues={{
              hasSolarDiverter: hasSolarDiverter || ('no' as const),
            }}
            validateOnChange
          >
            {({ handleSubmit, isValid, isSubmitting, values }) => (
              <Form onSubmit={handleSubmit}>
                <Field name="hasSolarDiverter">
                  {({ field, meta }: FieldProps<SolarDiverterOption>) => (
                    <RadioWrapper
                      className={cx({
                        selected: field.value === 'yes',
                      })}
                    >
                      <Form.Check
                        type="radio"
                        name={field.name}
                        id="more-less-than"
                        value={'yes'}
                        checked={field.value === 'yes'}
                        label="Yes, I have a solar diverter"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setHasSolarDiverter(e.target.value);
                          field.onChange(e);
                        }}
                        onBlur={field.onBlur}
                        className={cx({ error: !!meta.error })}
                      />
                    </RadioWrapper>
                  )}
                </Field>
                <Field name="hasSolarDiverter">
                  {({ field, meta }: FieldProps<SolarDiverterOption>) => (
                    <RadioWrapper
                      className={cx({
                        selected: field.value === 'no',
                      })}
                    >
                      <Form.Check
                        type="radio"
                        name={field.name}
                        id="radio-less-than"
                        value={'no'}
                        checked={field.value === 'no'}
                        label="No, I don't have a solar diverter"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setHasSolarDiverter(e.target.value);
                          field.onChange(e);
                        }}
                        onBlur={field.onBlur}
                        className={cx({ error: !!meta.error })}
                      />
                    </RadioWrapper>
                  )}
                </Field>
                {!isSubmitting ? (
                  <Button
                    variant="primary"
                    disabled={!isValid || isSubmitting || !values.hasSolarDiverter}
                    className="w-100"
                    type="submit"
                  >
                    Continue
                  </Button>
                ) : (
                  <LoopSpinner />
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default SolarDiverterContainer;
